import { PropsWithChildren, useCallback, useState } from 'react';
import { Accept, FileRejection, useDropzone } from 'react-dropzone';
import cn from 'classnames';
import { getAcceptTypes } from '@/shared/lib/utils/getAcceptTypes';
import type { FileInfo } from '@/shared/types/files';
import { NewFileItem } from './NewFileItem';
import { DataBaseFileItem } from './DataBaseFileItem';

export interface DropzoneProps extends PropsWithChildren {
  setFile?: (file: File | null) => void;
  setFiles?: (files: File[]) => void;
  file?: File | null;
  files?: File[];
  /** уже загруженные приватные файлы записи */
  dataBaseFiles?: FileInfo[];
  handleRemoveDataBaseFile?: (key: string) => void;
  withPreviewList?: boolean;
  multiple?: boolean;
  isSolid?: boolean;
  isImage?: boolean;
  isDocuments?: boolean;
  isScorm?: boolean;
  isVideo?: boolean;
  className?: string;
  accept?: Accept;
  maxSize?: number;
  onDropRejected?: (fileRejections: FileRejection[]) => void;
  disabled?: boolean;
}

export const Dropzone = (props: DropzoneProps) => {
  const {
    setFile,
    setFiles,
    file,
    files,
    dataBaseFiles = [],
    handleRemoveDataBaseFile,
    multiple = false,
    withPreviewList = false,
    children,
    isSolid = false,
    isImage = false,
    isDocuments = false,
    isScorm = false,
    isVideo = false,
    className,
    accept: acceptProp,
    maxSize,
    onDropRejected,
    disabled = false,
  } = props;
  const [fileList, setFileList] = useState<File[]>([]);
  const isControlledMultiple = multiple && files !== undefined;
  const isControlledSingle = !multiple && file !== undefined;
  const currentFiles = multiple ? (files ?? fileList) : file ? [file] : fileList;

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (multiple) {
        const newFiles = [...currentFiles, ...acceptedFiles];
        if (!isControlledMultiple) {
          setFileList(newFiles);
        }
        setFiles?.(newFiles);
      } else {
        const nextFile = acceptedFiles[0] ?? null;
        if (!isControlledSingle) {
          setFileList(nextFile ? [nextFile] : []);
        }
        setFile?.(nextFile);
      }
    },
    [currentFiles, isControlledMultiple, isControlledSingle, multiple, setFile, setFiles],
  );

  const handleRemoveFile = (fileToRemove: File) => {
    const updatedFiles = currentFiles.filter((file) => file !== fileToRemove);
    if (!isControlledMultiple && !isControlledSingle) {
      setFileList(updatedFiles);
    }
    if (multiple) {
      setFiles?.(updatedFiles);
    } else {
      setFile?.(null);
    }
  };

  const accept = acceptProp ?? getAcceptTypes({ isImage, isDocuments, isScorm, isVideo });
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: multiple ? undefined : 1,
    accept,
    maxSize,
    onDropRejected,
    disabled,
  });
  const areDataBaseFiles = dataBaseFiles.length > 0;
  const areNewFiles = currentFiles.length > 0;
  const isPreviewList = withPreviewList && (areDataBaseFiles || areNewFiles);

  const wrapperClassName = cn(
    'border-2 rounded p-5',
    disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
    isSolid ? 'border-solid border-[#E5E7EB]' : 'border-dashed border-[#E5E7EB]',
    className,
  );

  const wrapperStyle = {
    background: 'linear-gradient(90deg, #FFFFFF 0%, #FFF9F9 100%)',
  };

  return (
    <div className="flex flex-col gap-3">
      <div {...getRootProps({ className: wrapperClassName, style: wrapperStyle })}>
        <input {...getInputProps()} />
        <div className="grid place-items-center">{children}</div>
      </div>
      {isPreviewList && (
        <ul className="flex flex-col gap-0.5">
          {areDataBaseFiles &&
            dataBaseFiles.map((file) => (
              <DataBaseFileItem
                key={file.key}
                file={file}
                handleRemoveFile={handleRemoveDataBaseFile}
              />
            ))}
          {areNewFiles &&
            currentFiles.map((file) => (
              <NewFileItem
                key={`${file.name}${file.size}`}
                file={file}
                handleRemoveFile={handleRemoveFile}
              />
            ))}
        </ul>
      )}
    </div>
  );
};
